import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type MockJWOItem } from '@/mocks/billing';
import AppLayout from '@/components/feature/AppLayout';
import { jobWorkService } from '@/services/jobWorkService';
import { getData } from '@/services/FetchNodeServices';
import { useWarehouseStore } from '@/stores/warehouseStore';
import AddSubContractorModal from '../components/AddSubContractorModal';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { useUnits } from '@/hooks/useUnits';

interface FormState {
  subContractorId: string;
  jobDescription: string;
  date: string;
  expectedReturnDate: string;
  jobCharges: number;
  productionOrderId: string;
  notes: string;
  items: Omit<MockJWOItem, 'id' | 'jwoId' | 'receivedQty' | 'pendingQty' | 'rejectedQty'>[];
}

export default function JobWorkNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resubmitFromId = searchParams.get('resubmitFrom');

  const { selectedWarehouseId } = useWarehouseStore();

  const today = new Date().toISOString().slice(0, 10);

  // Dynamic Master & Resubmit States
  const [subContractors, setSubContractors] = useState<any[]>([]);
  const [productionOrders, setProductionOrders] = useState<any[]>([]);
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [existingJWO, setExistingJWO] = useState<any>(null);
  const [isAddSubModalOpen, setIsAddSubModalOpen] = useState(false);

  // Fetch Master Data on Mount & whenever active warehouse in header changes
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        // 1. Fetch dedicated sub-contractors for selected warehouse (no fallback to parties)
        const subRes = await jobWorkService.getSubContractors({ warehouseId: selectedWarehouseId });
        if (subRes && Array.isArray(subRes)) {
          setSubContractors(subRes);
        } else {
          setSubContractors([]);
        }

        const poRes = await getData('api/v1/manufacturing/production-orders');
        const poList = poRes?.data || (Array.isArray(poRes) ? poRes : []);
        const filteredPOs = selectedWarehouseId
          ? poList.filter((po: any) => !po.warehouseId && !po.warehouse_id || po.warehouseId === selectedWarehouseId || po.warehouse_id === selectedWarehouseId)
          : poList;
        setProductionOrders(filteredPOs);

        const itemUrl = selectedWarehouseId ? `api/v1/item?warehouse_id=${selectedWarehouseId}` : 'api/v1/item';
        const itemRes = await getData(itemUrl);
        const iList = itemRes?.data || (Array.isArray(itemRes) ? itemRes : []);
        setAvailableItems(iList);

        if (resubmitFromId) {
          const exJWO = await jobWorkService.getById(resubmitFromId);
          if (exJWO) {
            setExistingJWO(exJWO);
            setForm({
              subContractorId: exJWO.subContractorId,
              jobDescription: exJWO.jobDescription,
              date: today,
              expectedReturnDate: exJWO.expectedReturnDate,
              jobCharges: exJWO.jobCharges,
              productionOrderId: exJWO.productionOrderId || '',
              notes: exJWO.notes || '',
              items: exJWO.items.map((i: any) => ({
                itemId: i.itemId,
                itemName: i.itemName,
                hsnCode: i.hsnCode,
                sentQty: i.sentQty,
                unit: i.unit,
                rate: i.rate,
                totalValue: i.totalValue,
              })),
            });
          }
        }
      } catch (err) {
        console.error('Error fetching masters for job work creation:', err);
      }
    };
    fetchMasters();
  }, [selectedWarehouseId, resubmitFromId]);

  const [form, setForm] = useState<FormState>({
    subContractorId: '',
    jobDescription: '',
    date: today,
    expectedReturnDate: '',
    jobCharges: 0,
    productionOrderId: '',
    notes: '',
    items: [],
  });

  const subContractorOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select sub-contractor --' },
    ...subContractors.map((sc) => ({
      value: sc.id,
      label: sc.name,
      sublabel: sc.gstin ? `GSTIN: ${sc.gstin}` : (sc.phone || sc.city)
    }))
  ], [subContractors]);

  const productionOrderOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'None — standalone job work' },
    ...productionOrders.map((po) => ({
      value: po.id,
      label: `${po.po_number || po.poNumber || po.id} — ${po.product_name || po.productName || 'Production Order'}`
    }))
  ], [productionOrders]);

  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [itemQty, setItemQty] = useState(1);
  // Quantities are in the item's unit: decimals for Kg / m / L, whole numbers for Pcs
  const units = useUnits();
  const parseQtyFor = (value: string, unit?: string | null) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n <= 0) return 1;
    return units.decimalError(n, unit) ? Math.max(1, Math.round(n)) : Math.round(n * 1e6) / 1e6;
  };
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedSub = subContractors.find((sc) => sc.id === form.subContractorId);
  const selectedPO = form.productionOrderId ? productionOrders.find((po) => po.id === form.productionOrderId) : null;

  // Compute lead time
  const expectedReturn = useMemo(() => {
    if (!form.date) return '';
    const leadDays = selectedSub?.leadTimeDays || 7;
    const d = new Date(form.date);
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().slice(0, 10);
  }, [form.date, selectedSub]);

  // Filter searchable items
  const filteredSearchItems = useMemo(() => {
    if (!itemSearch) return availableItems.slice(0, 10);
    const q = itemSearch.toLowerCase();
    return availableItems.filter((it) => (it.name || '').toLowerCase().includes(q) || (it.code || '').toLowerCase().includes(q)).slice(0, 10);
  }, [itemSearch, availableItems]);

  // Total
  const totalMaterialValue = form.items.reduce((s, i) => s + i.totalValue, 0);

  const updateField = (field: keyof FormState, value: any) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-calc expected return when sub-contractor or date changes
      if (field === 'subContractorId' || field === 'date') {
        const sc = field === 'subContractorId' ? subContractors.find((s) => s.id === value) : selectedSub;
        const d = field === 'date' ? value : prev.date;
        if (sc && d) {
          const ret = new Date(d);
          ret.setDate(ret.getDate() + (sc.leadTimeDays || 7));
          next.expectedReturnDate = ret.toISOString().slice(0, 10);
        }
      }
      return next;
    });
  };

  const addItem = (searchItem: any) => {
    const qty = itemQty;
    const rate = parseFloat(
      searchItem.purchase_rate ||
      searchItem.purchaseRate ||
      searchItem.sale_rate ||
      searchItem.saleRate ||
      searchItem.rate ||
      searchItem.mrp ||
      0
    );
    const unit = searchItem.unit_name || searchItem.unit || searchItem.primary_unit || 'Pcs';
    const hsn = searchItem.hsn_code || searchItem.hsnCode || 'N/A';
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          itemId: searchItem.id,
          itemName: searchItem.name,
          hsnCode: hsn,
          sentQty: qty,
          unit: unit,
          rate,
          totalValue: qty * rate,
        },
      ],
    }));
    setItemSearch('');
    setItemQty(1);
    setShowItemDropdown(false);
  };

  const updateItem = (idx: number, field: string, value: any) => {
    setForm((prev) => {
      const items = [...prev.items];
      (items[idx] as any)[field] = value;
      if (field === 'sentQty' || field === 'rate') {
        items[idx].totalValue = items[idx].sentQty * items[idx].rate;
      }
      return { ...prev, items };
    });
  };

  const removeItem = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.subContractorId) errs.subContractorId = 'Please select a sub-contractor';
    if (!form.jobDescription.trim()) errs.jobDescription = 'Job description is required';
    if (!form.expectedReturnDate) errs.expectedReturnDate = 'Expected return date is required';
    if (form.items.length === 0) errs.items = 'Add at least one material item';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!validate()) return;
    try {
      const payload = {
        date: form.date,
        subContractorId: form.subContractorId,
        subContractorName: selectedSub?.name || '',
        subContractorGSTIN: selectedSub?.gstin || '',
        warehouseId: selectedWarehouseId || null,
        jobDescription: form.jobDescription,
        jobCharges: form.jobCharges,
        expectedReturnDate: form.expectedReturnDate,
        productionOrderId: form.productionOrderId || null,
        notes: form.notes || null,
        items: form.items.map((item) => ({
          itemId: item.itemId,
          itemName: item.itemName,
          hsnCode: item.hsnCode,
          sentQty: item.sentQty,
          unit: item.unit,
          rate: item.rate,
        })),
      };

      const res = await jobWorkService.create(payload);
      if (res && res.success) {
        navigate('/manufacturing/jobwork');
      }
    } catch (e) {
      console.error('Error saving draft via API:', e);
    }
  };

  const handleIssueChallan = async () => {
    if (!validate()) return;
    try {
      const payload = {
        date: form.date,
        subContractorId: form.subContractorId,
        subContractorName: selectedSub?.name || '',
        subContractorGSTIN: selectedSub?.gstin || '',
        warehouseId: selectedWarehouseId || null,
        jobDescription: form.jobDescription,
        jobCharges: form.jobCharges,
        expectedReturnDate: form.expectedReturnDate,
        productionOrderId: form.productionOrderId || null,
        notes: form.notes || null,
        items: form.items.map((item) => ({
          itemId: item.itemId,
          itemName: item.itemName,
          hsnCode: item.hsnCode,
          sentQty: item.sentQty,
          unit: item.unit,
          rate: item.rate,
        })),
      };

      const res = await jobWorkService.create(payload);
      if (res && res.success && res.data?.id) {
        navigate(`/manufacturing/jobwork/${res.data.id}`);
      }
    } catch (e) {
      console.error('Error issuing challan via API:', e);
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/manufacturing/jobwork')}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
                title="Back to Job Work Orders"
              >
                <i className="ri-arrow-left-line text-lg" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-[#1e293b]">
                  {existingJWO ? 'Edit & Resubmit Job Work Order' : 'New Job Work Order'}
                </h1>
                <p className="text-xs text-slate-500 font-medium mt-0.5">Send material out for subcontracting / job work</p>
              </div>
            </div>
          </div>

          {/* Resubmit banner */}
          {existingJWO && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-900">This job work order is being resubmitted</p>
                {existingJWO.notes && <p className="text-xs text-amber-700 mt-0.5 font-medium">Revision note: {existingJWO.notes}</p>}
              </div>
            </div>
          )}

          {/* Section 1 — Header */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e293b] mb-4 pb-2 border-b border-slate-100">Job Work Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Sub-Contractor <span className="text-rose-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddSubModalOpen(true)}
                    className="text-xs font-bold text-[#2563eb] hover:text-blue-700 cursor-pointer flex items-center gap-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-0.5 rounded-lg transition-colors"
                  >
                    <i className="ri-add-line" /> Quick Add
                  </button>
                </div>
                <SearchableSelect
                  value={form.subContractorId}
                  onChange={(val) => updateField('subContractorId', val)}
                  options={subContractorOptions}
                  placeholder="-- Select sub-contractor --"
                  searchPlaceholder="Search sub-contractor..."
                />
                {errors.subContractorId && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.subContractorId}</p>}
                {selectedSub && (
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500 font-medium">
                    <span>GSTIN: {selectedSub.gstin || 'N/A'}</span>
                    <span>Phone: {selectedSub.phone || 'N/A'}</span>
                    <span>City: {selectedSub.city || 'N/A'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Job Description <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={form.jobDescription}
                  onChange={(e) => updateField('jobDescription', e.target.value)}
                  rows={2}
                  placeholder="e.g. Heat treatment and hardening of Steel Rods..."
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium resize-none ${errors.jobDescription ? 'border-rose-300' : 'border-slate-200'}`}
                />
                {errors.jobDescription && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.jobDescription}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField('date', e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Expected Return Date
                </label>
                <input
                  type="date"
                  value={form.expectedReturnDate || expectedReturn}
                  onChange={(e) => updateField('expectedReturnDate', e.target.value)}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium cursor-pointer ${errors.expectedReturnDate ? 'border-rose-300' : 'border-slate-200'}`}
                />
                {errors.expectedReturnDate && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.expectedReturnDate}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Job Charges (₹)</label>
                <input
                  type="number"
                  value={form.jobCharges || ''}
                  onChange={(e) => updateField('jobCharges', parseInt(e.target.value) || 0)}
                  placeholder="Vendor charge for processing"
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Link to Production Order (Optional)</label>
                <SearchableSelect
                  value={form.productionOrderId}
                  onChange={(val) => updateField('productionOrderId', val)}
                  options={productionOrderOptions}
                  placeholder="None — standalone job work"
                  searchPlaceholder="Search production order..."
                />
                {selectedPO && (
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500 font-medium">
                    <span>Status: {selectedPO.status}</span>
                    <span>Planned: {selectedPO.plannedQty} {selectedPO.unit}</span>
                    <span>Warehouse: {selectedPO.warehouseName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 3 — Materials to Send */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
              <h2 className="text-sm font-bold text-[#1e293b]">Materials to Send</h2>
              {errors.items && <p className="text-xs text-rose-500 font-medium">{errors.items}</p>}
            </div>

            {/* Add Item */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <input
                  type="text"
                  value={itemSearch}
                  onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                  onFocus={() => setShowItemDropdown(true)}
                  placeholder="Search item by name, code, or barcode..."
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
                {showItemDropdown && itemSearch && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-30 max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {filteredSearchItems.length === 0 ? (
                      <p className="px-3.5 py-2.5 text-xs text-slate-500 bg-white">No items found</p>
                    ) : (
                      filteredSearchItems.map((it) => (
                        <button
                          key={it.id}
                          onClick={() => addItem(it)}
                          className="w-full text-left px-3.5 py-2.5 text-sm bg-white hover:bg-blue-50 text-slate-900 cursor-pointer flex items-center justify-between transition-colors"
                        >
                          <span className="font-semibold text-slate-900">{it.name}</span>
                          <span className="text-xs text-slate-500">
                            {it.code ? `${it.code} — ` : ''}₹{it.purchase_rate || it.purchaseRate || it.sale_rate || it.saleRate || it.mrp || 0} — Stock: {it.stock ?? it.currentStock ?? 0} {it.unit_name || it.unit || 'Pcs'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <input
                type="number"
                min={0}
                step="any"
                value={itemQty}
                // Whole numbers for the unit of the item "Add Item" will pick (first match)
                onChange={(e) => setItemQty(parseQtyFor(e.target.value, filteredSearchItems[0]?.unit_name || filteredSearchItems[0]?.unit))}
                className="w-20 px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                placeholder="Qty"
              />
              <button
                onClick={() => {
                  if (itemSearch && filteredSearchItems.length > 0) {
                    addItem(filteredSearchItems[0]);
                  }
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-add-line text-base" /> Add Item
              </button>
            </div>

            {/* Items Table */}
            {form.items.length > 0 && (
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-200">
                      <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item</th>
                      <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">HSN</th>
                      <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty</th>
                      <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Unit</th>
                      <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Rate (₹)</th>
                      <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Value (₹)</th>
                      <th className="text-center px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {form.items.map((item, idx) => {
                      const searchItem = availableItems.find((si) => si.id === item.itemId);
                      const isLowStock = searchItem && searchItem.currentStock < item.sentQty;
                      return (
                        <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-3.5 py-2.5">
                            <span className="font-bold text-[#1e293b]">{item.itemName}</span>
                            {isLowStock && (
                              <span className="inline-flex items-center gap-0.5 ml-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-bold">
                                <i className="ri-error-warning-line" /> Low stock ({searchItem?.currentStock} avail)
                              </span>
                            )}
                          </td>
                          <td className="px-3.5 py-2.5 text-slate-500 font-mono text-xs">{item.hsnCode}</td>
                          <td className="px-3.5 py-2.5 text-right">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={item.sentQty}
                              onChange={(e) => updateItem(idx, 'sentQty', parseQtyFor(e.target.value, item.unit))}
                              className="w-20 px-2 py-1.5 text-sm text-right border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-bold"
                            />
                          </td>
                          <td className="px-3.5 py-2.5 text-slate-600 font-medium">{item.unit}</td>
                          <td className="px-3.5 py-2.5 text-right">
                            <input
                              type="number"
                              min={0}
                              value={item.rate}
                              step="any"
                              onChange={(e) => updateItem(idx, 'rate', Math.max(0, parseFloat(e.target.value) || 0))}
                              className="w-24 px-2 py-1.5 text-sm text-right border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                            />
                          </td>
                          <td className="px-3.5 py-2.5 text-right font-bold text-[#1e293b]">₹{item.totalValue.toLocaleString('en-IN')}</td>
                          <td className="px-3.5 py-2.5 text-center">
                            <button
                              onClick={() => removeItem(idx)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50 hover:text-rose-700 cursor-pointer transition-colors"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50/70">
                      <td colSpan={5} className="px-3.5 py-3 text-right font-bold text-slate-700">Total Material Value:</td>
                      <td className="px-3.5 py-3 text-right font-bold text-[#2563eb] text-base">₹{totalMaterialValue.toLocaleString('en-IN')}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Section 4 — Notes */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e293b] mb-4 pb-2 border-b border-slate-100">Notes</h2>
            <textarea
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              placeholder="Additional instructions, quality requirements, special handling..."
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleSaveDraft}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold border border-slate-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line text-base" /> Save as Draft
            </button>
            <button
              onClick={handleIssueChallan}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
            >
              <i className="ri-file-list-3-line text-base" /> Save & Issue Challan
            </button>
          </div>
        </div>
      </div>

      <AddSubContractorModal
        isOpen={isAddSubModalOpen}
        onClose={() => setIsAddSubModalOpen(false)}
        onSuccess={(newSub) => {
          setSubContractors((prev) => [newSub, ...prev]);
          updateField('subContractorId', newSub.id);
        }}
      />
    </AppLayout>
  );
}